import { useContext } from 'react';

import { useRouter } from '@eneco/next';
import { GrowthBookContext } from '@growthbook/growthbook-react';

import {
  defaultContractTypeExperimentFeatureToggleKey,
  defaultContractTypeExperimentVariant,
} from '../../../growthbook/experiment.helpers';

/**
 * Gates the default contract-type pre-selection on the offer step behind a GrowthBook experiment.
 *
 * Control (`OFF`): returns `false` — no offer is pre-selected and the user must make a choice.
 * Variant (`ON`): returns `true` — the offer step pre-selects the first offer returned by the backend.
 *
 * Reads the GrowthBook context directly instead of `useFeatureValue`: the latter throws
 * "Missing or invalid GrowthBookProvider" when no provider is present (e.g. the contract
 * extension flow renders the offer step without a GrowthBookProvider).
 */
export const useDefaultContractTypeExperiment = (): boolean => {
  const { query } = useRouter();
  const { growthbook } = useContext(GrowthBookContext);

  // QA overrides are checked before the feature read so forced sessions record no exposure
  // and therefore stay out of the experiment results.
  if (query.forceDefaultContractType === 'true') return true;
  if (query.disableExperiments === 'true') return false;

  // Defaults to the control: if the experiments API fails or is slow, nobody gets a pre-selection.
  const variant = growthbook?.getFeatureValue(defaultContractTypeExperimentFeatureToggleKey, null);

  return variant === defaultContractTypeExperimentVariant;
};
